import React, { useEffect, useState } from "react";

import { Spinner, Text } from "@performyard/py-components";

import { CalendarSelectionModal } from "./CalendarSelectionModal";
import { DisconnectionModal } from "./DisconnectionModal";
import { GoogleCalendarIcon } from "./GoogleCalendarIcon";
import { OutlookCalendarIcon } from "./OutlookCalendarIcon";
import { ProviderConnectionCard } from "./ProviderConnectionCard";

import {
	useCalendarIntegrationOAuthUrl,
	useDisconnectCalendarIntegration,
	useUserCalendarIntegrationStatus,
	useUserCalendars,
} from "../../../../../api/hooks/defs/meetings-integrations";

import { CALENDAR_PROVIDERS, CalendarProvider } from "../config";

/** Calendar section of the meetings-integrations view. Parent gates FF + admin toggle. */
export const CalendarSection = (): React.ReactElement => {
	const [showCalendarModal, setShowCalendarModal] = useState(false);
	const [showDisconnectConfirmation, setShowDisconnectConfirmation] = useState(false);

	// Google calendar auth initiation
	const { initiateCalendarIntegrationAuth: initiateGoogleAuth, isLoading: isGoogleAuthLoading } =
		useCalendarIntegrationOAuthUrl({
			provider: CALENDAR_PROVIDERS.GOOGLE as CalendarProvider,
		});

	// Outlook calendar auth initiation
	const { initiateCalendarIntegrationAuth: initiateOutlookAuth, isLoading: isOutlookAuthLoading } =
		useCalendarIntegrationOAuthUrl({
			provider: CALENDAR_PROVIDERS.OUTLOOK as CalendarProvider,
		});

	// User calendar status
	const {
		data: userCalendarStatus,
		isLoading: isStatusLoading,
		refetch: refetchStatus,
	} = useUserCalendarIntegrationStatus();

	// Disconnect calendar integration
	const { mutateAsync: disconnectCalendarIntegration, isPending: isDisconnecting } =
		useDisconnectCalendarIntegration();

	// Fetch calendars when user is connected
	const isConnected = userCalendarStatus?.isConnected;
	const provider = userCalendarStatus?.provider;
	const {
		data: calendarsData,
		isLoading: isCalendarsLoading,
		error: calendarsError,
	} = useUserCalendars({
		provider: provider as "google" | "outlook",
		// Strict boolean — undefined would trip the hook's `enabled = true` default.
		enabled: !!isConnected && !!provider,
	});

	// The calendars request can clear a dead integration server-side (e.g. a revoked token); refetch
	// status so the card reflects the disconnect instead of showing a stale "Connected" state.
	useEffect(() => {
		if (calendarsError) {
			refetchStatus();
		}
	}, [calendarsError, refetchStatus]);

	const handleConnect = (selectedProvider: CalendarProvider) => {
		if (selectedProvider === CALENDAR_PROVIDERS.GOOGLE) {
			initiateGoogleAuth();
		} else if (selectedProvider === CALENDAR_PROVIDERS.OUTLOOK) {
			initiateOutlookAuth();
		}
	};

	const handleDisconnect = () => {
		setShowDisconnectConfirmation(true);
	};

	const handleConfirmDisconnect = async () => {
		await disconnectCalendarIntegration();
		setShowDisconnectConfirmation(false);
	};

	// Find the selected calendar name and determine if we should show manage button
	const selectedCalendar = calendarsData?.calendars?.find((cal) => cal.id === userCalendarStatus?.selectedCalendarId);
	const connectedCalendarName = selectedCalendar?.name;
	const isConnectedCalendarPrimary = selectedCalendar?.primary || false;
	const shouldShowManageButton = (calendarsData?.calendars?.length || 0) > 1;

	const handleCloseModal = () => {
		setShowCalendarModal(false);
		// Refetch status to check if calendar was selected
		refetchStatus();
	};

	if (isCalendarsLoading) {
		return (
			<div className="flex h-[208px] w-full">
				<div className="flex h-full w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col gap-4">
			{isStatusLoading ? (
				<div className="flex h-full w-full items-center justify-center">
					<Spinner size={32} />
				</div>
			) : (
				<>
					<div className="flex w-[656px] flex-col gap-1">
						<Text size="paragraph-2" weight="semi-bold">
							Calendar
						</Text>
						<Text size="paragraph-3" weight="regular">
							Connect your calendar to sync meetings created in PerformYard directly to Google Calendar or
							Outlook.
						</Text>
					</div>
					<div className="flex flex-col gap-4">
						<ProviderConnectionCard
							provider="google"
							displayName="Google Calendar"
							icon={<GoogleCalendarIcon size={40} />}
							isConnected={!!(isConnected && provider === "google")}
							isDisabled={
								isGoogleAuthLoading ||
								isStatusLoading ||
								isDisconnecting ||
								// Disable if another provider is connected
								(userCalendarStatus?.isConnected && provider !== "google")
							}
							onConnect={() => handleConnect(CALENDAR_PROVIDERS.GOOGLE as CalendarProvider)}
							onDisconnect={handleDisconnect}
							connectedAs={provider === "google" ? connectedCalendarName : null}
							connectedSuffix={
								provider === "google" && isConnectedCalendarPrimary ? (
									<span className="text-neutral-6"> (Primary)</span>
								) : null
							}
							secondaryAction={
								provider === "google" && shouldShowManageButton
									? { label: "Manage", onClick: () => setShowCalendarModal(true) }
									: undefined
							}
						/>
						<ProviderConnectionCard
							provider="outlook"
							displayName="Outlook Calendar"
							icon={<OutlookCalendarIcon size={40} />}
							isConnected={!!(isConnected && provider === "outlook")}
							isDisabled={
								isOutlookAuthLoading ||
								isStatusLoading ||
								isDisconnecting ||
								// Disable if another provider is connected
								(userCalendarStatus?.isConnected && provider !== "outlook")
							}
							onConnect={() => handleConnect(CALENDAR_PROVIDERS.OUTLOOK as CalendarProvider)}
							onDisconnect={handleDisconnect}
							connectedAs={provider === "outlook" ? connectedCalendarName : null}
							connectedSuffix={
								provider === "outlook" && isConnectedCalendarPrimary ? (
									<span className="text-neutral-6"> (Primary)</span>
								) : null
							}
							secondaryAction={
								provider === "outlook" && shouldShowManageButton
									? { label: "Manage", onClick: () => setShowCalendarModal(true) }
									: undefined
							}
						/>
					</div>
					<CalendarSelectionModal isOpen={showCalendarModal} onClose={handleCloseModal} />
					<DisconnectionModal
						isOpen={showDisconnectConfirmation}
						title={`Disconnect ${
							provider === "google"
								? "Google Calendar"
								: provider === "outlook"
									? "Outlook Calendar"
									: "Calendar"
						}?`}
						bodyText="New meetings won't sync to your calendar. Existing meetings won't be affected."
						isDisconnecting={isDisconnecting}
						handleConfirmDisconnect={handleConfirmDisconnect}
						handleCancelDisconnect={() => setShowDisconnectConfirmation(false)}
					/>
				</>
			)}
		</div>
	);
};
